import React, { useMemo, useCallback } from 'react';

import {
    makeStyles,
    Button,
    createTheme,
} from '@material-ui/core';
import {
    DataGrid,
    esES,
    GridOverlay,
} from '@material-ui/data-grid';
import { useParos } from './ParosContext';
import { useGeneral } from '../../Context/GeneralContext';
import { claseColorLlamada, CLASE_SIN_COLOR } from '../../utils/coloresLlamada';

const defaultTheme = createTheme();

const useStyles = makeStyles(
    (theme) => ({
        root: {
            height: '100%',
            maxHeight: '100%',
            width: '96%',
            marginTop: 10,
            justifyContent: 'center',
            display: 'flex',
            flexDirection: 'column',
            background: '#ffffff',
            boxShadow: '1px 2px 4px #00000014',
            borderRadius: 3,
        },
        root1: {
            flexDirection: 'column',
            '& .ant-empty-img-1': {
                fill: theme.palette.type === 'light' ? '#aeb8c2' : '#262626',
            },
            '& .ant-empty-img-2': {
                fill: theme.palette.type === 'light' ? '#f5f5f7' : '#595959',
            },
            '& .ant-empty-img-3': {
                fill: theme.palette.type === 'light' ? '#dce0e6' : '#434343',
            },
            '& .ant-empty-img-4': {
                fill: theme.palette.type === 'light' ? '#fff' : '#1c1c1c',
            },
            '& .ant-empty-img-5': {
                fillOpacity: theme.palette.type === 'light' ? '0.8' : '0.08',
                fill: theme.palette.type === 'light' ? '#f5f5f5' : '#fff',
            },
        },
        label: {
            marginTop: theme.spacing(1),
            textAlign: 'center',
            fontSize: '26px',
            fontWeight: 600,
            fontFamily: 'WorkSans',
            letterSpacing: '0px',
            opacity: 1,
        },
    }),
    { defaultTheme }
);

const TablaParosComponent = React.memo(function TablaParosComponent({ paros, intervalos, loadingTable, seleccionFila, setSeleccionFila, setOpenP, setFolio }) {
    const classes = useStyles();

    const tableColumns = useMemo(() => [
        {
            field: 'area',
            headerName: 'Area',
            flex: 1,
            minWidth: 80,
            headerAlign: 'center',
            align: 'center',
            renderCell: renderArea,
        },
        {
            field: 'nombre',
            headerName: 'Nombre',
            flex: 1.5,
            minWidth: 120,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'fechaHoraIni',
            headerName: 'Fecha Ini.',
            flex: 1.8,
            minWidth: 160,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: ({ value }) => {
                if (!value) return '';
                const d = new Date(value.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(value) ? value : `${value}Z`);
                return d.toLocaleString('es-MX', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true });
            },
        },
        {
            field: 'fechaHoraFin',
            headerName: 'Fecha Fin.',
            flex: 1.8,
            minWidth: 160,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: ({ value }) => {
                if (!value) return '';
                const d = new Date(value.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(value) ? value : `${value}Z`);
                return d.toLocaleString('es-MX', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true });
            },
        },
        {
            field: 'duracion',
            headerName: 'Duración',
            flex: 1.3,
            minWidth: 110,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'nombreCelda',
            headerName: 'Celda',
            flex: 1.2,
            minWidth: 110,
            headerAlign: 'center',
            align: 'center',
        },
        {
            field: 'acciones',
            headerName: 'Acciones',
            flex: 1.2,
            minWidth: 140,
            align: 'center',
            headerAlign: 'center',
            renderCell: (cellValues) => {
                if (cellValues.row.nombre === 'Paro') {
                    return (
                        <Button
                            variant="contained"
                            className="table-button"
                            color="primary"
                            onClick={(event) => {
                                setFolio(cellValues.id);
                                setOpenP(true);
                            }}
                        >
                            <h4 className="table-button-text">Categorizar Paro</h4>
                        </Button>
                    );
                }
            },
        },
    ], [setFolio, setOpenP]);

    // El badge combina dos campos independientes del tipo de llamada: la inicial sale
    // del área de atención y el color del catálogo de colores de la llamada
    function renderArea(params) {
        const { areaNombre, color } = params.row;
        const clase = claseColorLlamada(color);

        if (!areaNombre || !clase) {
            return (
                <div className={CLASE_SIN_COLOR} title="Sin Categorizar">
                    <h1>S</h1>
                </div>
            );
        }

        return (
            <div className={clase} title={areaNombre}>
                <h1>{areaNombre.charAt(0).toUpperCase()}</h1>
            </div>
        );
    }

    function CustomNoRowsOverlay() {
        const classes = useStyles();

        return (
            <GridOverlay className={classes.root1}>
                <svg width="120" height="100" viewBox="0 0 184 152" aria-hidden focusable="false">
                    <g fill="none" fillRule="evenodd">
                        <g transform="translate(24 31.67)">
                            <ellipse
                                className="ant-empty-img-5"
                                cx="67.797"
                                cy="106.89"
                                rx="67.797"
                                ry="12.668"
                            />
                            <path
                                className="ant-empty-img-1"
                                d="M122.034 69.674L98.109 40.229c-1.148-1.386-2.826-2.225-4.593-2.225h-51.44c-1.766 0-3.444.839-4.592 2.225L13.56 69.674v15.383h108.475V69.674z"
                            />
                            <path
                                className="ant-empty-img-2"
                                d="M33.83 0h67.933a4 4 0 0 1 4 4v93.344a4 4 0 0 1-4 4H33.83a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z"
                            />
                            <path
                                className="ant-empty-img-3"
                                d="M42.678 9.953h50.237a2 2 0 0 1 2 2V36.91a2 2 0 0 1-2 2H42.678a2 2 0 0 1-2-2V11.953a2 2 0 0 1 2-2zM42.94 49.767h49.713a2.262 2.262 0 1 1 0 4.524H42.94a2.262 2.262 0 0 1 0-4.524zM42.94 61.53h49.713a2.262 2.262 0 1 1 0 4.525H42.94a2.262 2.262 0 0 1 0-4.525zM121.813 105.032c-.775 3.071-3.497 5.36-6.735 5.36H20.515c-3.238 0-5.96-2.29-6.734-5.36a7.309 7.309 0 0 1-.222-1.79V69.675h26.318c2.907 0 5.25 2.448 5.25 5.42v.04c0 2.971 2.37 5.37 5.277 5.37h34.785c2.907 0 5.277-2.421 5.277-5.393V75.1c0-2.972 2.343-5.426 5.25-5.426h26.318v33.569c0 .617-.077 1.216-.221 1.789z"
                            />
                        </g>
                        <path
                            className="ant-empty-img-3"
                            d="M149.121 33.292l-6.83 2.65a1 1 0 0 1-1.317-1.23l1.937-6.207c-2.589-2.944-4.109-6.534-4.109-10.408C138.802 8.102 148.92 0 161.402 0 173.881 0 184 8.102 184 18.097c0 9.995-10.118 18.097-22.599 18.097-4.528 0-8.744-1.066-12.28-2.902z"
                        />
                        <g className="ant-empty-img-4" transform="translate(149.65 15.383)">
                            <ellipse cx="20.654" cy="3.167" rx="2.849" ry="2.815" />
                            <path d="M5.698 5.63H0L2.898.704zM9.259.704h4.985V5.63H9.259z" />
                        </g>
                    </g>
                </svg>
                <div className={classes.label}>
                    En el momento no hay paros registrados
                </div>
            </GridOverlay>
        );
    }

    function CustomLoadingOverlay() {
        const classes = useStyles();
        return (
            <GridOverlay className={classes.root1}>
                <div className="paros-loading-overlay">
                    <div className="paros-loading-dots">
                        <span></span><span></span><span></span>
                    </div>
                    <div className={classes.label} style={{ fontSize: '20px' }}>
                        Buscando paros sin categorizar...
                    </div>
                </div>
            </GridOverlay>
        );
    }

    const getRowId = useCallback((row) => row.id || row.folio, []);

    return (
        <div className={classes.root}>
            <DataGrid
                key="paros-data-grid"
                getRowId={getRowId}
                components={{ NoRowsOverlay: CustomNoRowsOverlay, LoadingOverlay: CustomLoadingOverlay }}
                disableColumnFilter
                disableDensitySelector
                checkboxSelection={false}
                loading={loadingTable}
                columns={tableColumns}
                localeText={esES.props.MuiDataGrid.localeText}
                rows={paros}
                onSelectionModelChange={(newSelectionModel) => {
                    setSeleccionFila(newSelectionModel);
                }}
                selectionModel={seleccionFila}
                hideFooterSelectedRowCount={true}
                disableVirtualization={false}
                rowHeight={52}
            />
        </div>
    );
}, (prevProps, nextProps) => {
    // Comparar el contenido y no solo los folios: el área y la duración de un paro
    // cambian sin que cambien los ids, y la tabla debe reflejarlo
    const parosChanged = JSON.stringify(prevProps.paros) !== JSON.stringify(nextProps.paros);

    const loadingChanged = prevProps.loadingTable !== nextProps.loadingTable;
    const intervalosChanged = prevProps.intervalos !== nextProps.intervalos;
    const seleccionChanged = JSON.stringify(prevProps.seleccionFila) !== JSON.stringify(nextProps.seleccionFila);
    
    const shouldUpdate = parosChanged || loadingChanged || intervalosChanged || seleccionChanged;
    
    return !shouldUpdate; // Retornar true si NO debe actualizar (props son iguales)
});

const TablaParos = function TablaParos() {
    const { setOpenP, setFolio, paros, intervalos, loadingTable } = useGeneral();
    const { seleccionFila, setSeleccionFila } = useParos();
    
    return (
        <TablaParosComponent
            paros={paros}
            intervalos={intervalos}
            loadingTable={loadingTable}
            seleccionFila={seleccionFila}
            setSeleccionFila={setSeleccionFila}
            setOpenP={setOpenP}
            setFolio={setFolio}
        />
    );
};

export default TablaParos;
